import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { forkJoin, of, ReplaySubject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { createGuid } from '../../../core/helpers/utility';
import { INotes } from '../../../core/models/campaigns.model';
import { ISalesRep } from '../../../core/models/datafeed.models';
import { ITask } from '../../../core/models/tasks.models';
import { ILead } from '../../../core/models/lead.models';
import { DataFeedService } from '../../api/datafeed.service';
import { TasksApiService } from '../../../pages/tasks/tasks.apiservice';
import { NOTES_TYPE, ROLES_TYPE } from '../../../core/helpers/constants';
import { Utility } from '../../utility';
import * as moment from 'moment';
import { IUser } from '../../../core/models/auth.models';
import { ClientsApiService } from '../../../pages/clients/clients.apiservice';
import { TagsApiService } from '../../../pages/tags/tags.apiservice';
import { ITag } from '../../../core/models/tags.models';

@Component({
  selector: 'modal-add-call-note',
  templateUrl: './add-call-note.component.html',
  styleUrls: ['./add-call-note.component.scss']
})
export class AddCallNoteModal implements OnInit, OnDestroy {

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Output() oSaveClicked = new EventEmitter<any>();
  @Output() oTaskUpdated = new EventEmitter<void>();

  allAssignees: Array<ISalesRep> = new Array<ISalesRep>();
  tasksDataNotCompleted: Array<ITask> = new Array<ITask>();
  clientNotes: Array<INotes> = new Array<INotes>();
  campaignNotes: Array<INotes> = new Array<INotes>();
  allUsers: Array<IUser> = new Array<IUser>();
  clientData: any = {};
  isTiered: boolean = false;
  tierName: string = '';
  clientTags: Array<ITag> = [];

  get tierBannerClass(): string {
    const name = (this.tierName ?? '').toLowerCase();
    if (name.includes('basic')) return 'tier-banner--basic';
    if (name.includes('premium')) return 'tier-banner--premium';
    if (name.includes('standard')) return 'tier-banner--standard';
    return 'tier-banner--standard';
  }

  activeNotesSection: 'client' | 'campaign' | 'tasks' = 'client';

  addClientCallForm!: FormGroup;
  
  submitted = false;

  @Input() iClientId: string = '';
  @Input() iType: number = 1;
  @Input() iIsAdmin: boolean = true;
  @Input() iTask: ITask | null = null;
  @Input() set iNote(note: INotes) {
    if (note) {
      this.addClientCallForm.patchValue(note);
      if (note.noteFiles)
        this.files = note.noteFiles;
    }
  }

  constructor(
    private dataFeedService: DataFeedService,
    private taskApiService: TasksApiService,
    private clientsApiService: ClientsApiService,
    private tagService: TagsApiService,
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder) {
    this.addClientCallForm = this.formBuilder.group({
      id: [null],
      type: [null],
      clientId: [null],
      clientFirstName: [null],
      clientLastName: [null],
      dueDate: [null],
      assignedUserId: [null],
      assignedUserFirstName: [null],
      assignedUserLastName: [null],
      taskIdToComplete: [null],
      callNotes: [null, [Validators.required]],
      nextAction: [null, [Validators.required]],
      addTaskDate:[moment.utc().local().add(14, 'days').startOf('day').toDate(), [Validators.required]],
      nextTaskAssignedUserId: [null],
      sendMissedCallEmail: [false]
    });
  }
  get f() { return this.addClientCallForm.controls; }

  activeUserId = '';
  assignedUserFullname = '';

  private patchHiddenIdentityFields(): void {

    const clientFirstName =
      (this.clientData as any)?.firstName ??
      (this.clientData as any)?.client?.firstName ??
      null;

    const clientLastName =
      (this.clientData as any)?.lastName ??
      (this.clientData as any)?.client?.lastName ??
      null;

    // Assigned user: prefer full user list when available, otherwise fall back to parsing assignedUserName.
    const assignedUserId =
      this.addClientCallForm?.controls?.['assignedUserId']?.value ??
      (this.iTask as any)?.assignedUserId ??
      null;

    const assignedUser = assignedUserId ? (this.allUsers ?? []).find(u => u.id === assignedUserId) : null;

    const parsedAssignedName = (((this.iTask as any)?.assignedUserName ?? '') as string).trim();
    const parsedParts = parsedAssignedName ? parsedAssignedName.split(/\s+/).filter(Boolean) : [];
    const parsedFirst = parsedParts.length > 0 ? parsedParts[0] : null;
    const parsedLast = parsedParts.length > 1 ? parsedParts.slice(1).join(' ') : null;

    const assignedUserFirstName =
      (assignedUser as any)?.firstName ??
      this.addClientCallForm?.controls?.['assignedUserFirstName']?.value ??
      parsedFirst;

    const assignedUserLastName =
      (assignedUser as any)?.lastName ??
      this.addClientCallForm?.controls?.['assignedUserLastName']?.value ??
      parsedLast;

    this.addClientCallForm.patchValue(
      {
        clientFirstName,
        clientLastName,
        assignedUserFirstName,
        assignedUserLastName,
      },
      { emitEvent: false }
    );
  }

  taskUpdateSaving = false;
  taskUpdateSuccess = false;
  taskUpdateError: string | null = null;

  ngOnInit() {

    setTimeout(() => {
      this.patchTaskFieldsFromTrigger();

      if (!this.iClientId) return;
      this.clientsApiService.getClientDetails(this.iClientId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          // console.log('Client details response:', response);
          this.clientData = response.data;
          this.loadTierBanner(this.clientData?.id ?? this.iClientId);

          // Default next-call assignee to the client DMS (only when not already selected).
          const dmsId = (this.clientData as any)?.dmsId ?? (this.clientData as any)?.client?.dmsId;
          const nextTaskAssignedUserId = this.addClientCallForm?.controls?.['nextTaskAssignedUserId']?.value;
          if ((!nextTaskAssignedUserId || (typeof nextTaskAssignedUserId === 'string' && nextTaskAssignedUserId.trim() === '')) && dmsId) {
            this.addClientCallForm.patchValue({ nextTaskAssignedUserId: dmsId }, { emitEvent: false });
          }
        });

      this.dataFeedService.getNotes(this.iClientId, NOTES_TYPE.Clients)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          this.clientNotes = response.data.filter((note: INotes) => note.campaignId == null);
          this.campaignNotes = response.data.filter((note: INotes) => note.campaignId != null);
        });

      this.dataFeedService.getAllTaskAssignees()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          if (response.good) {
            this.allUsers = [...response.data].map((i) => { i.fullName = i.firstName + ' ' + i.lastName; return i; });
            this.patchTaskFieldsFromTrigger();
          }
        });
      this.taskApiService.getTasksByClient(this.iClientId)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((response: any) => {
            this.tasksDataNotCompleted = [...response.data.filter((x: ITask) => x.isCompleted === null || x.isCompleted === false)];
          });
    }, 0);
  }

  setActiveNotesSection(section: 'client' | 'campaign' | 'tasks'): void {
    this.activeNotesSection = section;
  }

  private extractTierName(tagName: string): string {
    if (!tagName) return '';

    const partsPipe = tagName.split('|').map(p => p.trim()).filter(Boolean);
    if (partsPipe.length >= 2) {
      return partsPipe[1];
    }

    const lower = tagName.toLowerCase();
    const idx = lower.indexOf('tiered pricing');
    if (idx >= 0) {
      const after = tagName.slice(idx + 'tiered pricing'.length).replace(/^\s*[-:]\s*/, '').trim();
      return after || tagName;
    }

    return tagName;
  }

  private findTierTag(tags: Array<ITag>): ITag | undefined {
    return tags.find(t => {
      const name = (t?.name ?? '').toLowerCase();
      const categories = (t?.categories ?? '').toLowerCase();
      return name.includes('tiered pricing') || (categories === 'pricing' && name.includes('tier'));
    });
  }

  private loadTierBanner(clientId: string | null | undefined): void {
    if (!clientId) {
      this.clientTags = [];
      this.isTiered = false;
      this.tierName = '';
      return;
    }

    this.tagService.getAssociatedTags(clientId, 2)
      .pipe(
        map((response: any) => Array.isArray(response?.data) ? (response.data as Array<ITag>) : []),
        catchError(() => of([] as Array<ITag>)),
        takeUntil(this.destroyed$)
      )
      .subscribe((clientTags: Array<ITag>) => {
        this.clientTags = clientTags;
        const tierTag = this.findTierTag(clientTags);
        this.isTiered = !!tierTag;
        this.tierName = tierTag ? this.extractTierName(tierTag.name) : '';
      });
  }

  private patchTaskFieldsFromTrigger(): void {
    if (!this.iTask) return;

    const dueDate = this.iTask.dueDate ? new Date(this.iTask.dueDate) : null;
    const assignedUserId = this.iTask.assignedUserId ?? null;

    // Patch without emitting value changes (keeps this isolated from any other listeners)
    this.addClientCallForm.patchValue(
      {
        dueDate,
        assignedUserId,
        taskIdToComplete: (this.iTask as any)?.id ?? null,
      },
      { emitEvent: false }
    );

    // Keep hidden name fields in sync with the triggering task.
    this.patchHiddenIdentityFields();
  }

  async updateTriggerTask(): Promise<void> {
    if (!this.iTask?.id) {
      this.taskUpdateError = 'No task context available to update.';
      return;
    }

    this.taskUpdateSuccess = false;
    this.taskUpdateError = null;

    const dueDateValue = this.addClientCallForm.controls['dueDate']?.value;
    const assignedUserId = this.addClientCallForm.controls['assignedUserId']?.value;

    if (!dueDateValue && !assignedUserId) {
      this.taskUpdateError = 'Select a due date and/or assigned user first.';
      return;
    }

    const dueDate = dueDateValue ? Utility.getTaskDueDateFromDateTime(dueDateValue) : null;
    const assignedUser = assignedUserId ? this.allUsers.find(u => u.id === assignedUserId) : null;
    const assignedUserName = assignedUser
      ? (((assignedUser as any)?.fullName as string | undefined)
        ?? `${(assignedUser as any)?.firstName ?? ''} ${(assignedUser as any)?.lastName ?? ''}`.trim())
      : '';

    this.taskUpdateSaving = true;
    try {
      // Load the full task so the API receives all fields it expects
      const task: ITask = await new Promise<ITask>((resolve, reject) => {
        this.taskApiService.getTaskDetails(this.iTask!.id)
          .pipe(takeUntil(this.destroyed$))
          .subscribe({
            next: (response: any) => {
              const data = response?.data ?? response;
              if (!data) {
                reject(new Error('Task details not found.'));
                return;
              }
              resolve(data as ITask);
            },
            error: (err: any) => reject(err)
          });
      });

      const updated: any = { ...task };
      if (dueDate) updated.dueDate = dueDate;
      if (assignedUserId) {
        updated.assignedUserId = assignedUserId;
        if (assignedUserName) updated.assignedUserName = assignedUserName;
      }

      await new Promise<void>((resolve, reject) => {
        this.taskApiService.saveTask(updated)
          .pipe(takeUntil(this.destroyed$))
          .subscribe({
            next: () => resolve(),
            error: (err: any) => reject(err)
          });
      });

      // Update local task context so reopening doesn't revert
      this.iTask = {
        ...this.iTask,
        dueDate: dueDate ? dueDate : this.iTask.dueDate,
        assignedUserId: assignedUserId ? assignedUserId : this.iTask.assignedUserId,
        assignedUserName: assignedUserName ? assignedUserName : this.iTask.assignedUserName,
      };

      this.taskUpdateSuccess = true;
      this.oTaskUpdated.emit();
      this.activeModal.close('task-updated');
    } catch (e: any) {
      this.taskUpdateError = e?.message ?? 'Failed to update task.';
    } finally {
      this.taskUpdateSaving = false;
    }
  }

  onSubmit() {
    this.submitted = true;
    if (this.addClientCallForm.invalid)
      return;
    if (this.f.id.value === null)
      this.f.id.setValue(createGuid());

    // Ensure identity fields are populated right before emitting.
    this.patchHiddenIdentityFields();

    var request = { ...this.addClientCallForm.value };

    // Task quick-update fields are handled separately and should not be submitted as part of the campaign check.
    delete request.dueDate;
    delete request.assignedUserId;

    request = this.trimObjectStrings(request);

    request.clientId = this.iClientId;
    request.type = this.iType;
    request.addFiles = this.files.filter(x => x.link === undefined).map(x => { return { name: x.name, content: x.content } });
    request.removeFiles = this.removedFiles;
    request.addTaskDate = Utility.getTaskDueDateFromDateTime(request.addTaskDate);

    // If this modal was opened from a task (DMS dashboard), ensure the backend gets a valid GUID here.
    if (!request.taskIdToComplete && (this.iTask as any)?.id) {
      request.taskIdToComplete = (this.iTask as any).id;
    }
    if (typeof request.taskIdToComplete === 'string' && request.taskIdToComplete.trim() === '') {
      request.taskIdToComplete = null;
    }

    this.oSaveClicked.emit(request);
  }

  // This function will recursively iterate through the object
  trimObjectStrings(obj: any): any {
    // Iterate over each property of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        if (typeof value === 'string') {
          // Trim the string if the value is a string
          obj[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Recursively call the function if the value is an object (or array)
          this.trimObjectStrings(value);
        }
      }
    }
    return obj;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  files: any[] = [];
  removedFiles: any[] = [];
  onSelect(event: any) {
    for (var i = 0; i < event.addedFiles.length; i++) {
      var file = event.addedFiles[i];
      var that = this.files;
      this.readFile(file).then((fileContents: any) => {
        file.content = fileContents;
        that.push(file);
      })
    }
  }

  onRemove(event: any) {
    this.files.splice(this.files.indexOf(event), 1);
    if (event.id) {
      this.removedFiles.push(event.id);
    }
  }

  readFile(file: File): any {
    return new Promise<string | ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        var result: any = (e.target as FileReader).result;
        return resolve(result);
      };
      reader.onerror = e => {
        console.error(`FileReader failed on file ${file.name}.`);
        return reject(null);
      };
      if (!file) {
        console.error('No file to read.');
        return reject(null);
      }
      reader.readAsDataURL(file);
    });
  }

}